import { takeLatest, call, select, cancel, put } from 'redux-saga/effects';

import * as inspectReq from '../requests/inspection-req';
import { selectAppRequestEmergencyContactInfo } from '../../data/selectors';
import { APPOINTMENT_PROGRESS_TYPES } from '../progress-types';
import {
  APPOINTMENT_INSPECTION,
  URGENT_VISIT_TYPE_CODE,
  INLINE_TYPES,
  EIR_APPOINTMENT_TYPES,
} from '../../../constants';
import {
  selectIsEirVisitTypeCode,
  makeSelectInspectionById,
  selectProgressVisitTypeCode,
  selectProgressCareUserSkipInsurance,
  selectProgressOrganizationTenantSkipInsurance,
  selectProgressCareUserHasPrimaryCareProvider,
  selectProgressCareUserId,
} from '../selectors';

import {
  redirectToConsentForm,
  redirectToEmergencyContact,
  redirectToFullInsuranceForm,
  redirectToPatientForm,
  redirectToPharmacy,
  redirectToVisitDetails,
  redirectToAdditionalInformation,
  redirectToHealthInfoProvider,
  redirectToHealthInfo,
  redirectToAppointmentOverview,
  redirectToProviders,
  redirectToGuarantorInformation,
} from '../../../utils/appointments/redirect-utils';
import { selectShowPaperworkAppointmentQuestionStep } from '../../paperwork/selectors/paperwork-additional-info';
import { selectPaperworkAppointmentUserId } from '../../paperwork/selectors/paperwork-get-appointment-details';
import * as userDetails from '../requests/user-details-req';
import { PAPERWORK } from '../../../constants/routes';

const { PROFILE, LOCAL_PROFILE } = APPOINTMENT_INSPECTION;

export function* handleShowEmergencyContactStep() {
  const conditionForShowingEmergencyContact = yield select(
    selectAppRequestEmergencyContactInfo,
  );

  if (conditionForShowingEmergencyContact) {
    yield call(redirectToEmergencyContact);
    yield cancel();
  }

  yield call(determineNextStepAfterEmergencyContact);
}

export function* verifyInspection() {
  const profileForm = yield select(makeSelectInspectionById(PROFILE));
  const localProfileForm = yield select(
    makeSelectInspectionById(LOCAL_PROFILE),
  );

  if (profileForm && !profileForm.completed) {
    yield call(redirectToPatientForm);
    yield cancel();
  }

  if (localProfileForm && !localProfileForm.completed) {
    yield call(redirectToPatientForm);
    yield cancel();
  }

  yield call(handleShowEmergencyContactStep);
}

function* handleShowInsuranceFormStep() {
  const selectedOrganizationTenantSkipInsurance = yield select(
    selectProgressOrganizationTenantSkipInsurance,
  );

  const careUserSkipInsurance = yield select(
    selectProgressCareUserSkipInsurance,
  );

  const shouldCheckInsurance =
    careUserSkipInsurance === false &&
    selectedOrganizationTenantSkipInsurance === false;

  if (shouldCheckInsurance) {
    yield call(redirectToFullInsuranceForm);
  } else {
    yield call(determineNextStepAfterInsuranceForm);
  }
}

// primary care provider (pcp/health-info-provider) step is shown only for urgent virtual visit
// and eir visit types (but has different UI)
function* handleShowPrimaryCareProviderStep() {
  const visitTypeCode = yield select(selectProgressVisitTypeCode);

  const isUrgentVirtualVisit = visitTypeCode === URGENT_VISIT_TYPE_CODE;

  const careUserHasPrimaryCareProvider = yield select(
    selectProgressCareUserHasPrimaryCareProvider,
  );

  if (isUrgentVirtualVisit && careUserHasPrimaryCareProvider) {
    yield call(handleShowAdditionalInformationStep);
  } else {
    yield call(redirectToHealthInfoProvider);
  }
}

function* determineNextStepAfterEmergencyContact() {
  const visitTypeCode = yield select(selectProgressVisitTypeCode);

  const isInlineVisitType = INLINE_TYPES.includes(visitTypeCode);
  const isEirVisit = EIR_APPOINTMENT_TYPES.includes(visitTypeCode);

  const isUrgentVirtualVisit = visitTypeCode === URGENT_VISIT_TYPE_CODE;

  if (isEirVisit) {
    yield call(redirectToGuarantorInformation);

    yield cancel();
  }

  if (isInlineVisitType || isUrgentVirtualVisit) {
    yield call(handleShowInsuranceFormStep);

    yield cancel();
  }

  yield call(redirectToVisitDetails);
}

export function* determineNextStepAfterGuarantorInformation() {
  const getCareUserId = yield select(selectProgressCareUserId);
  const paperworkUserId = yield select(selectPaperworkAppointmentUserId);

  const { pathname } = window.location;

  const isPaperwork = pathname.includes(PAPERWORK);

  const userId = isPaperwork ? paperworkUserId : getCareUserId;

  if (userId) {
    yield put(userDetails.actions.send({ id: userId }));
  }

  if (!isPaperwork) {
    yield call(redirectToVisitDetails);
  }
}

function* determineNextStepAfterInsuranceForm() {
  const visitTypeCode = yield select(selectProgressVisitTypeCode);

  const isEirVisitType = yield select(selectIsEirVisitTypeCode);

  const isInlineVisitType = INLINE_TYPES.includes(visitTypeCode);

  const isUrgentVirtualVisit = visitTypeCode === URGENT_VISIT_TYPE_CODE;

  if (isInlineVisitType) {
    yield call(redirectToVisitDetails);

    yield cancel();
  }

  if (isUrgentVirtualVisit) {
    yield call(redirectToVisitDetails);

    yield cancel();
  }

  if (isEirVisitType) {
    yield call(redirectToPharmacy);

    yield cancel();
  }

  yield call(handleShowAdditionalInformationStep);
}

function* determineNextStepAfterVisitDetails() {
  const visitTypeCode = yield select(selectProgressVisitTypeCode);

  const isInlineVisitType = INLINE_TYPES.includes(visitTypeCode);

  const isUrgentVirtualVisit = visitTypeCode === URGENT_VISIT_TYPE_CODE;

  if (isInlineVisitType) {
    yield call(handleShowAdditionalInformationStep);

    yield cancel();
  }

  if (isUrgentVirtualVisit) {
    yield call(redirectToConsentForm);

    yield cancel();
  }

  // for eir visit types, office visit and virtual visit type
  yield call(redirectToProviders);
}

// pharmacy step showed only for urgent virtual visit and eir visit types
function* determineNextStepAfterPharmacy() {
  yield call(handleShowPrimaryCareProviderStep);
}

// health info provider step showed only for urgent virtual visit and eir visit types
function* determineNextStepAfterHealthInfoProvider() {
  yield call(handleShowAdditionalInformationStep);
}

export function* handleShowAdditionalInformationStep() {
  const showPaperworkAppointmentQuestionStep = yield select(
    selectShowPaperworkAppointmentQuestionStep,
  );

  const visitTypeCode = yield select(selectProgressVisitTypeCode);

  const isInlineVisitType = INLINE_TYPES.includes(visitTypeCode);

  const isUrgentVirtualVisit = visitTypeCode === URGENT_VISIT_TYPE_CODE;

  if (showPaperworkAppointmentQuestionStep) {
    yield call(redirectToAdditionalInformation);

    yield cancel();
  }

  if (isUrgentVirtualVisit) {
    // health info step showed only for urgent virtual visit type
    yield call(redirectToHealthInfo);
    yield cancel();
  }

  if (isInlineVisitType) {
    yield call(redirectToAppointmentOverview);
    yield cancel();
  }

  // for eir visit types, office visit and virtual visit type
  yield call(redirectToConsentForm);
}

function* determineNextStepAfterAppointmentProviders() {
  yield call(handleShowInsuranceFormStep);
}

export default function* inspectAllSaga() {
  yield takeLatest(inspectReq.consts.SUCCEED, verifyInspection);

  yield takeLatest(
    APPOINTMENT_PROGRESS_TYPES.DETERMINE_NEXT_STEP_AFTER_EMERGENCY_CONTACT,
    determineNextStepAfterEmergencyContact,
  );

  yield takeLatest(
    APPOINTMENT_PROGRESS_TYPES.DETERMINE_NEXT_STEP_AFTER_GUARANTOR_INFORMATION,
    determineNextStepAfterGuarantorInformation,
  );

  yield takeLatest(
    APPOINTMENT_PROGRESS_TYPES.DETERMINE_NEXT_STEP_AFTER_INSURANCE_FORM,
    determineNextStepAfterInsuranceForm,
  );

  yield takeLatest(
    APPOINTMENT_PROGRESS_TYPES.DETERMINE_NEXT_STEP_AFTER_VISIT_DETAILS,
    determineNextStepAfterVisitDetails,
  );

  yield takeLatest(
    APPOINTMENT_PROGRESS_TYPES.DETERMINE_NEXT_STEP_AFTER_PHARMACY,
    determineNextStepAfterPharmacy,
  );

  yield takeLatest(
    APPOINTMENT_PROGRESS_TYPES.DETERMINE_NEXT_STEP_AFTER_HEALTH_INFO_PROVIDER_STEP,
    determineNextStepAfterHealthInfoProvider,
  );

  yield takeLatest(
    APPOINTMENT_PROGRESS_TYPES.DETERMINE_NEXT_STEP_AFTER_APPOINTMENT_PROVIDERS,
    determineNextStepAfterAppointmentProviders,
  );
}



// WEBPACK FOOTER //
// ./src/redux/appointmentForm/sagas/inspectAll-saga.js